import {
  changeCountsLabel,
  compareVersions,
  countChanges,
  screensOf,
  type ChangelogScreenName,
  type ChangelogVersion,
} from '@cockpit/shared';

/**
 * Lot BA « Nouveautés » (maquette nouveautes-prevenir-1910, onglets « Dans l'appli » et « Page Nouveautés ») :
 * logique pure de l'encart « Nouvelle version », du « Quoi de neuf », du point vert des onglets et de la page.
 */

export const NOUVEAUTES_PATH = '/nouveautes';

/**
 * Onglet de la navigation pour chaque écran du journal (`name` de `TABS`). Les écrans sans onglet (Page client, Sous-traitants,
 * Mode écran, Notifications, Sur téléphone, Partout) n'allument aucun point.
 */
export const TAB_OF_SCREEN: Record<ChangelogScreenName, string | null> = {
  'À faire': 'afaire',
  Support: 'support',
  Projets: 'projets',
  Conseiller: 'conseiller',
  Chiffres: 'chiffres',
  Trésorerie: 'tresorerie',
  Dépenses: 'depenses',
  Carte: 'carte',
  Agenda: 'agenda',
  Apporteurs: 'apporteurs',
  Retours: 'retours',
  Réglages: 'reglages',
  'Page client': null,
  'Sous-traitants': null,
  'Mode écran': null,
  Notifications: null,
  // Lot BF : « Mon compte Google » (webhook Google Chat), sans onglet.
  'Mon compte': null,
  'Sur téléphone': null,
  Partout: null,
};

/** Onglets dont l'écran a des nouveautés dans ces versions (un écran inconnu de cette appli : aucun). */
export function tabsWithNews(versions: readonly ChangelogVersion[]): string[] {
  const tabs = new Set<string>();
  for (const screen of screensOf(versions)) {
    const tab = TAB_OF_SCREEN[screen as ChangelogScreenName];
    if (tab) tabs.add(tab);
  }
  return [...tabs];
}

/** Versions visibles d'une plage (après `from`, jusqu'à `to` compris), la plus récente d'abord. */
export function versionsBetween(
  versions: readonly ChangelogVersion[],
  from: string,
  to: string,
): ChangelogVersion[] {
  return versions
    .filter((v) => compareVersions(v.version, from) > 0 && compareVersions(v.version, to) <= 0)
    .sort((a, b) => compareVersions(b.version, a.version));
}

/**
 * L'encart « Nouvelle version » : rien si le serveur n'est pas plus récent que l'appli ouverte, ou si les versions
 * entre les deux sont toutes techniques (rien de visible : pas d'encart pour rien) ; sinon le résumé d'une ligne.
 */
export function updateOffer(
  bundleVersion: string,
  serverVersion: string,
  versions: readonly ChangelogVersion[],
): { version: string; summary: string } | null {
  if (compareVersions(serverVersion, bundleVersion) <= 0) return null;
  const range = versionsBetween(versions, bundleVersion, serverVersion);
  if (range.length === 0) return null;
  const counts = changeCountsLabel(countChanges(range));
  const screens = screensOf(range);
  const where =
    screens.length > 0 ? ` (${screens.slice(0, 3).join(', ')}${screens.length > 3 ? '…' : ''})` : '';
  return { version: serverVersion, summary: `${capitalize(counts)}${where}. Rafraîchis pour l'avoir.` };
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** En-tête du « Quoi de neuf » : « VERSION 1.3.0 · 21/10/2026 », ou « VERSIONS 1.2.0 À 1.3.0 » regroupées. */
export function whatsNewHeading(versions: readonly ChangelogVersion[]): string {
  if (versions.length === 0) return '';
  const sorted = [...versions].sort((a, b) => compareVersions(a.version, b.version));
  const last = sorted.at(-1)!;
  if (sorted.length === 1) return `Version ${last.version} · ${frenchDate(last.date)}`;
  return `Versions ${sorted[0]!.version} à ${last.version}`;
}

/** 2026-10-21 → 21/10/2026. */
export function frenchDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** « mardi 21 octobre 2026 » (page Nouveautés). */
export function longDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  });
}

/** Mémoire locale (par membre) des onglets à point vert, jusqu'à ce que la personne les ouvre. */
export function newsTabsKey(userId: string): string {
  return `cockpit.nouveautes.onglets.${userId}`;
}

export function readNewsTabs(storage: Pick<Storage, 'getItem'> | null, userId: string): string[] {
  try {
    const raw = storage?.getItem(newsTabsKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

export function writeNewsTabs(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null,
  userId: string,
  tabs: readonly string[],
): void {
  try {
    if (tabs.length === 0) storage?.removeItem(newsTabsKey(userId));
    else storage?.setItem(newsTabsKey(userId), JSON.stringify([...new Set(tabs)]));
  } catch {
    // Stockage indisponible (navigation privée) : le point disparaît au prochain rechargement.
  }
}
